import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import PawnPhotoThumbs from './pawn/PawnPhotoThumbs';

function verificationDocUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

function last10Digits(phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.length > 10 && digits.startsWith('91')) digits = digits.slice(-10);
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [pendingEmiData, setPendingEmiData] = useState({ activeEmis: [], totalPendingAmount: 0, emiCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState({});
  const [witnessHistory, setWitnessHistory] = useState(null);
  const [sellerIntakeHistory, setSellerIntakeHistory] = useState(null);
  const [repairJobs, setRepairJobs] = useState([]);
  const [pawnHistory, setPawnHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('customer'); // 'customer', 'seller', 'witness', ...
  const [phoneTests, setPhoneTests] = useState([]);
  const [creditData, setCreditData] = useState(null);
  const [creditError, setCreditError] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  
  // Manual EMI completion states
  const [showEmiCompletionModal, setShowEmiCompletionModal] = useState(false);
  const [selectedEmiId, setSelectedEmiId] = useState(null);
  const [completionPin, setCompletionPin] = useState('');
  const [completingEmi, setCompletingEmi] = useState(false);
  const [verification, setVerification] = useState(null);
  const [kycAadharDigits, setKycAadharDigits] = useState('');
  const [kycPanDigits, setKycPanDigits] = useState('');
  
  // Loan Application States
  const [loanForm, setLoanForm] = useState({
    loanType: 'personal',
    loanPurpose: '',
    loanAmount: '',
    tenure: 6,
    collateral: { type: 'none', description: '', value: '' },
    witness: { witnessName: '', witnessPhone: '' }
  });
  const [loanApplicationLoading, setLoanApplicationLoading] = useState(false);
  const [loanApplicationMessage, setLoanApplicationMessage] = useState('');
  const [customerLoansFull, setCustomerLoansFull] = useState([]);
  const [expandedLoanId, setExpandedLoanId] = useState(null);
  const [markingKyc, setMarkingKyc] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      
      // Get customer details
      const customerResponse = await axios.get(`${config.API_URL}/customers/${id}`);
      
      // If customer doesn't have a voucherRedemptions array, add one
      if (!customerResponse.data.voucherRedemptions) {
        console.log('Creating empty voucherRedemptions array');
        customerResponse.data.voucherRedemptions = [];
      }
      
      setCustomer(customerResponse.data);
      setEditedCustomer(customerResponse.data);
      try {
        const phoneTestsRes = await axios.get(`${config.API_URL}/customers/${id}/phone-tests`);
        setPhoneTests(phoneTestsRes.data?.tests || []);
      } catch {
        setPhoneTests([]);
      }

      try {
        const verRes = await axios.get(`${config.API_URL}/verification/admin/customer/${id}`);
        if (verRes.data?.success && verRes.data.verification) {
          setVerification(verRes.data.verification);
          const ad = verRes.data.verification?.aadharCard?.aadharNumber;
          if (ad) setKycAadharDigits(ad);
          const pn = verRes.data.verification?.panCard?.panNumber;
          if (pn) setKycPanDigits(pn);
        } else {
          setVerification(null);
        }
      } catch {
        setVerification(null);
      }
      
      // Get pending EMIs for this customer
      const pendingResponse = await axios.get(`${config.API_URL}/customers/${id}/pending-emis`);
      setPendingEmiData(pendingResponse.data);
      
      // Get witness history if available
      try {
        const witnessResponse = await axios.get(`${config.API_URL}/customers/${id}/witness-history`);
        setWitnessHistory(witnessResponse.data);
      } catch (witnessError) {
        console.error('Error fetching witness history:', witnessError);
      }

      try {
        const sellerRes = await axios.get(`${config.API_URL}/customers/${id}/seller-intake-history`);
        setSellerIntakeHistory(sellerRes.data);
      } catch (sellerErr) {
        console.error('Error fetching seller intake history:', sellerErr);
        setSellerIntakeHistory(null);
      }

      try {
        const repairRes = await axios.get(`${config.API_URL}/repairs/customer/${id}`);
        setRepairJobs(repairRes.data || []);
      } catch (repairErr) {
        console.error('Error fetching repair history:', repairErr);
        setRepairJobs([]);
      }

      try {
        const pawnRes = await axios.get(`${config.API_URL}/customers/${id}/pawn-history`);
        setPawnHistory(pawnRes.data);
      } catch (pawnErr) {
        console.error('Error fetching pawn history:', pawnErr);
        setPawnHistory(null);
      }

      // Loan History: always load from Loan collection (embedded customer.loans often empty)
      try {
        const phone = customerResponse.data?.phone;
        if (phone) {
          const loanRes = await axios.get(
            `${config.API_URL}/enhanced-loans/customer/${encodeURIComponent(phone)}`
          );
          const apiLoans = loanRes.data?.loans || [];
          setCustomerLoansFull(apiLoans);
          if (apiLoans.length) {
            const mapped = apiLoans.map((loan) => ({
              date: loan.startDate || loan.createdAt || loan.disbursementDate,
              loanId: loan._id,
              loanType: loan.loanType,
              loanAmount: loan.loanAmount,
              totalAmount: loan.totalAmount,
              status: loan.status,
              riskLevel: loan.riskLevel === 'very_high' ? 'high' : loan.riskLevel,
            }));
            customerResponse.data.loans = mapped;
            setCustomer((prev) => (prev ? { ...prev, loans: mapped } : { ...customerResponse.data, loans: mapped }));
            setEditedCustomer((prev) =>
              prev ? { ...prev, loans: mapped } : { ...customerResponse.data, loans: mapped }
            );
          }
        }
      } catch (loanErr) {
        console.error('Error fetching loan history:', loanErr);
        setCustomerLoansFull([]);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load customer details');
      setLoading(false);
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedCustomer({ ...editedCustomer, [name]: value });
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${config.API_URL}/customers/${id}`, {
        name: editedCustomer.name,
        phone: editedCustomer.phone,
        email: editedCustomer.email,
        address: editedCustomer.address,
        idProof: editedCustomer.idProof,
        notes: editedCustomer.notes
      });
      setCustomer(editedCustomer);
      setEditMode(false);
      fetchCustomerDetails();
    } catch (err) {
      setError('Failed to update customer');
      console.error(err);
    }
  };

  const handleMarkAsWitness = async () => {
    try {
      await axios.patch(`${config.API_URL}/customers/${id}`, {
        witnessProfile: { isWitness: true }
      });
      fetchCustomerDetails();
    } catch (err) {
      alert('Failed to mark as witness');
      console.error(err);
    }
  };

  const openWitnessProfile = async (witness) => {
    if (!witness) return;
    const witnessId = witness.witnessId ? String(witness.witnessId) : '';
    if (witnessId) {
      navigate(`/customer/${witnessId}`);
      return;
    }
    const phone = last10Digits(witness.witnessPhone);
    if (!phone) {
      alert('Is witness ki profile ID/phone nahi mili');
      return;
    }
    try {
      const res = await axios.get(`${config.API_URL}/customers/search/phone/${encodeURIComponent(phone)}`);
      const list = Array.isArray(res.data) ? res.data : [];
      const found = list[0];
      if (found?._id) navigate(`/customer/${found._id}`);
      else alert('Witness profile nahi mili');
    } catch (err) {
      alert('Witness profile nahi mili');
      console.error(err);
    }
  };

  const renderWitnessLink = (witness) => {
    if (!witness?.witnessName) {
      return <span className="text-muted">None</span>;
    }
    const canOpen = !!(witness.witnessId || witness.witnessPhone);
    if (!canOpen) {
      return <span className="text-primary">{witness.witnessName}</span>;
    }
    return (
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-underline fw-semibold"
        onClick={() => openWitnessProfile(witness)}
        title="Witness profile kholo"
      >
        {witness.witnessName}
        {witness.witnessPhone ? (
          <span className="d-block small text-muted fw-normal">{witness.witnessPhone}</span>
        ) : null}
      </button>
    );
  };

  const fetchCreditScore = async () => {
    if (!customer?.phone) return;
    try {
      setCreditLoading(true);
      setCreditError('');
      const phone = String(customer.phone).replace(/\D/g, '').slice(-10);
      const res = await axios.get(`${config.API_URL}/credit-score/${phone}`);
      if (res.data?.success) setCreditData(res.data);
      else setCreditError(res.data?.message || 'Credit score load failed');
    } catch (err) {
      console.error('Credit score fetch error:', err);
      setCreditError(err.response?.data?.message || err.message || 'Credit score load failed');
    } finally {
      setCreditLoading(false);
    }
  };

  const refreshVerification = async () => {
    try {
      const verRes = await axios.get(`${config.API_URL}/verification/admin/customer/${id}`);
      if (verRes.data?.success && verRes.data.verification) {
        setVerification(verRes.data.verification);
      }
    } catch {
      setVerification(null);
    }
  };

  const handleUploadVerificationPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await axios.post(`${config.API_URL}/verification/admin/customer/${id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshVerification();
      e.target.value = '';
      alert('Customer photo uploaded.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Upload failed');
    }
  };

  const handleUploadAadhar = async (e) => {
    e.preventDefault();
    const front = e.target.elements.aadharFront?.files?.[0];
    const back = e.target.elements.aadharBack?.files?.[0];
    if (!front && !back) {
      alert('Front ya back mein se kam se kam ek image chunein.');
      return;
    }
    try {
      const fd = new FormData();
      if (front) fd.append('front', front);
      if (back) fd.append('back', back);
      const digits = kycAadharDigits.replace(/\D/g, '').slice(-12);
      if (digits.length === 12) fd.append('aadharNumber', digits);
      await axios.post(`${config.API_URL}/verification/admin/customer/${id}/aadhar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshVerification();
      e.target.reset();
      alert('Aadhar images uploaded.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Upload failed');
    }
  };

  const handleUploadPan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('panImage', file);
      const pan = kycPanDigits.trim().toUpperCase();
      if (pan && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) fd.append('panNumber', pan);
      await axios.post(`${config.API_URL}/verification/admin/customer/${id}/pan`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshVerification();
      e.target.value = '';
      alert('PAN uploaded.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Upload failed');
    }
  };

  const handleSendInvoice = async (emiId) => {
    setSendingInvoice(true);
    setInvoiceResult(null);
    
    try {
      const response = await axios.post(`${config.API_URL}/invoices/send/${emiId}`);
      
      setInvoiceResult(response.data);
      
      // Auto-hide success message after 3 seconds
      if (response.data.success) {
        setTimeout(() => setInvoiceResult(null), 3000);
      }
      
    } catch (error) {
      console.error('Error sending invoice:', error);
      setInvoiceResult({
        success: false,
        message: error.response?.data?.message || 'Failed to send invoice'
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleDownloadInvoice = async (emiId) => {
    setDownloadingInvoice(true);
    
    try {
      const response = await axios.get(`${config.API_URL}/invoices/download/${emiId}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${emiId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // Manual EMI completion handler
  const handleManualEmiCompletion = (emiId) => {
    setSelectedEmiId(emiId);
    setCompletionPin('');
    setShowEmiCompletionModal(true);
  };

  const handleEmiCompletionSubmit = async () => {
    if (!completionPin) {
      alert('Please enter the PIN');
      return;
    }

    setCompletingEmi(true);
    
    try {
      const response = await axios.post(`${config.API_URL}/customers/${id}/complete-emi`, {
        emiId: selectedEmiId,
        pin: completionPin
      });
      
      if (response.data.success) {
        alert('EMI marked as completed successfully!');
        setShowEmiCompletionModal(false);
        setCompletionPin('');
        setSelectedEmiId(null);
        // Refresh customer data
        fetchCustomerDetails();
      } else {
        alert('Failed to complete EMI: ' + response.data.message);
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setCompletingEmi(false);
    }
  };

  const handleLoanInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setLoanForm((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setLoanForm((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const isKycVerified = () => {
    const v = verification?.verificationStatus;
    const k = customer?.kycDetails?.kycStatus;
    return v === 'verified' || k === 'verified';
  };

  const hasKycDocsReady = () => {
    const v = verification;
    return Boolean(
      v?.customerPhoto &&
        v?.aadharCard?.frontImage &&
        v?.aadharCard?.backImage &&
        v?.panCard?.image
    );
  };

  const handleMarkKycVerified = async () => {
    if (!hasKycDocsReady()) {
      alert('Pehle Photo + Aadhar front/back + PAN upload karein (Customer tab → Edit → KYC documents).');
      setActiveTab('customer');
      return;
    }
    try {
      setMarkingKyc(true);
      await axios.put(`${config.API_URL}/verification/admin/customer/${id}/status`, {
        status: 'verified',
        note: 'Verified from Customer Detail for loan apply',
        adminId: 'admin',
      });
      await refreshVerification();
      await fetchCustomerDetails();
      alert('KYC verified! Ab Apply for Loan form khul jayega.');
      setActiveTab('loan-apply');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'KYC verify failed');
    } finally {
      setMarkingKyc(false);
    }
  };

  const handleLoanApplication = async (e) => {
    e.preventDefault();

    if (!isKycVerified()) {
      setLoanApplicationMessage('❌ KYC verification required before loan application. Please complete KYC first.');
      return;
    }

    if (verification?.verificationStatus === 'rejected' || customer?.kycDetails?.kycStatus === 'rejected') {
      setLoanApplicationMessage('❌ KYC verification rejected. Please update KYC details and try again.');
      return;
    }

    setLoanApplicationLoading(true);
    setLoanApplicationMessage('');

    try {
      const response = await fetch(`${config.API_URL}/enhanced-loans/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerPhone: customer.phone,
          ...loanForm
        })
      });

      const data = await response.json();

      if (response.ok) {
        let statusMessage = '';

        if (data.autoApproval) {
          statusMessage = `🎉 Loan Auto-Approved! WhatsApp notification sent to ${customer.phone}`;
        } else if (data.loan && data.loan.status === 'rejected') {
          statusMessage = `❌ Loan rejected due to credit score. WhatsApp notification sent to ${customer.phone}`;
        } else {
          statusMessage = `📋 Loan application submitted for admin review. WhatsApp confirmation sent to ${customer.phone}`;
        }

        setLoanApplicationMessage(
          statusMessage +
            `\n\nLoan Details:\n` +
            `Loan ID: ${data.loan._id}\n` +
            `Amount: ₹${data.loan.loanAmount.toLocaleString()}\n` +
            `Interest Rate: ${data.loan.interestRate}%\n` +
            `Monthly EMI: ₹${data.loan.monthlyInstallment.toLocaleString()}\n` +
            `Status: ${data.loan.status}\n` +
            `Credit Score: ${data.creditScore.creditScore}\n` +
            `Credit Grade: ${data.creditScore.creditGrade}\n\n` +
            `📱 WhatsApp notification sent to customer!`
        );

        setLoanForm({
          loanType: 'personal',
          loanPurpose: '',
          loanAmount: '',
          tenure: 6,
          collateral: { type: 'none', description: '', value: '' },
          witness: { witnessName: '', witnessPhone: '' }
        });
      } else {
        setLoanApplicationMessage('❌ Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error applying loan:', error);
      setLoanApplicationMessage('❌ Error submitting loan application');
    } finally {
      setLoanApplicationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!customer) {
    return <div className="alert alert-warning">Customer not found</div>;
  }

  // Sort purchases by date (newest first)
  const sortedPurchases = customer.purchases ? 
    [...customer.purchases].sort((a, b) => new Date(b.date) - new Date(a.date)) : 
    [];

  // Sort voucher redemptions by date (newest first)
  const sortedRedemptions = customer.voucherRedemptions && Array.isArray(customer.voucherRedemptions) ? 
    [...customer.voucherRedemptions].sort((a, b) => new Date(b.date) - new Date(a.date)) : 
    [];
    
  // Sort witnessed purchases by date (newest first)
  const sortedWitnessedPurchases = customer.witnessedPurchases ? 
    [...customer.witnessedPurchases].sort((a, b) => new Date(b.date) - new Date(a.date)) : 
    [];

  // Check if customer has witness profile
  const hasWitnessProfile = customer.witnessProfile?.isWitness || 
                           (customer.witnessedPurchases && customer.witnessedPurchases.length > 0);

  return (
    <div className="customer-detail-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          Customer Profile
          {customer.isRiskyCustomer && (
            <span className="badge bg-danger ms-2" title="Pehle NPA settle hua — EMI par extra caution">
              ⚠️ Risky Customer
            </span>
          )}
          {hasWitnessProfile && (
            <span className="badge bg-success ms-2">Witness</span>
          )}
        </h2>
        <div>
          <Link to="/customers" className="btn btn-secondary me-2">
            Back to Customers
          </Link>
          <button 
            className="btn btn-primary me-2" 
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Cancel Edit' : 'Edit Profile'}
          </button>
          {!customer.witnessProfile?.isWitness && (
            <button className="btn btn-warning" onClick={handleMarkAsWitness}>
              Mark as Witness
            </button>
          )}
        </div>
      </div>

      {customer.isRiskyCustomer && (
        <div className="alert alert-danger mb-4">
          <h5 className="alert-heading">⚠️ Risky Customer</h5>
          <p className="mb-2 small">
            Is customer ka pehle EMI NPA settle / write-off hua hai. Nayi EMI ya loan dene se pehle management approval zaroori hai.
          </p>
          {(customer.riskyCustomerHistory || []).length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm table-bordered bg-white mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Pending</th>
                    <th>Recovered</th>
                    <th>Loss</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(customer.riskyCustomerHistory || [])].reverse().map((h, i) => (
                    <tr key={i}>
                      <td>{h.flaggedAt ? new Date(h.flaggedAt).toLocaleDateString('en-IN') : '—'}</td>
                      <td>{h.productName || '—'}</td>
                      <td>₹{(h.originalPendingAmount || 0).toLocaleString('en-IN')}</td>
                      <td>₹{(h.settlementAmount || 0).toLocaleString('en-IN')}</td>
                      <td className="text-danger">₹{(h.lossAmount || 0).toLocaleString('en-IN')}</td>
                      <td>{h.notes || h.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editMode ? (
        <div className="card mb-4">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">Edit Customer Information</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleUpdateCustomer}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={editedCustomer.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone"
                    value={editedCustomer.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={editedCustomer.email || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">ID Proof</label>
                  <input
                    type="text"
                    className="form-control"
                    name="idProof"
                    value={editedCustomer.idProof || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Address</label>
                <textarea
                  className="form-control"
                  name="address"
                  value={editedCustomer.address || ''}
                  onChange={handleInputChange}
                  rows="2"
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  name="notes"
                  value={editedCustomer.notes || ''}
                  onChange={handleInputChange}
                  rows="2"
                ></textarea>
              </div>

              <div className="text-end mb-4">
                <button type="submit" className="btn btn-success">Save Changes</button>
              </div>
            </form>

              <hr />
              <h6 className="mb-3">KYC documents (upload)</h6>
              <p className="small text-muted">Photo, Aadhar, PAN yahan upload karein — profile view par &quot;View document&quot; se dekho.</p>
              <div className="mb-3">
                <label className="form-label">Customer photo</label>
                <input type="file" accept="image/*" className="form-control" onChange={handleUploadVerificationPhoto} />
              </div>
              <form onSubmit={handleUploadAadhar} className="mb-3 border rounded p-3">
                <label className="form-label">Aadhar (front / back)</label>
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <input type="file" name="aadharFront" accept="image/*" className="form-control form-control-sm" />
                  </div>
                  <div className="col-md-6">
                    <input type="file" name="aadharBack" accept="image/*" className="form-control form-control-sm" />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label small">Aadhar number (optional, 12 digits)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    maxLength={14}
                    value={kycAadharDigits}
                    onChange={(e) => setKycAadharDigits(e.target.value)}
                    placeholder="12 digits"
                  />
                </div>
                <button type="submit" className="btn btn-sm btn-outline-primary">Upload Aadhar</button>
              </form>
              <div className="mb-3">
                <label className="form-label">PAN card image</label>
                <input type="text" className="form-control form-control-sm mb-1" placeholder="PAN e.g. ABCDE1234F" value={kycPanDigits} onChange={(e) => setKycPanDigits(e.target.value.toUpperCase())} maxLength={10} />
                <input type="file" accept="image/*" className="form-control" onChange={handleUploadPan} />
              </div>
              <div className="alert alert-light border mb-0">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="small">
                    <strong>Verification status:</strong>{' '}
                    <span className={`badge ${
                      verification?.verificationStatus === 'verified' ? 'bg-success' :
                      verification?.verificationStatus === 'rejected' ? 'bg-danger' :
                      verification?.verificationStatus === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'
                    }`}>
                      {verification?.verificationStatus || 'incomplete'}
                    </span>
                    {!hasKycDocsReady() && (
                      <span className="text-muted ms-2">Photo + Aadhar F/B + PAN required</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={markingKyc || verification?.verificationStatus === 'verified'}
                    onClick={handleMarkKycVerified}
                  >
                    {markingKyc ? 'Saving…' : verification?.verificationStatus === 'verified' ? 'KYC Verified ✓' : 'Mark KYC Verified'}
                  </button>
                </div>
              </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header bg-dark text-white">
                <h5 className="mb-0">Personal Information</h5>
              </div>
              <div className="card-body">
                <table className="table table-borderless">
                  <tbody>
                    <tr>
                      <th width="30%">Name:</th>
                      <td>{customer.name}</td>
                    </tr>
                    <tr>
                      <th>Phone:</th>
                      <td>{customer.phone}</td>
                    </tr>
                    <tr>
                      <th>Email:</th>
                      <td>{customer.email || 'Not provided'}</td>
                    </tr>
                    <tr>
                      <th>Address:</th>
                      <td>{customer.address || 'Not provided'}</td>
                    </tr>
                    <tr>
                      <th>ID Proof:</th>
                      <td>{customer.idProof || 'Not provided'}</td>
                    </tr>
                    <tr>
                      <th>Notes:</th>
                      <td>{customer.notes || 'No notes'}</td>
                    </tr>
                    <tr>
                      <th>KYC documents</th>
                      <td className="small">
                        {verification?.customerPhoto?.path && (
                          <div><a href={verificationDocUrl(verification.customerPhoto.path)} target="_blank" rel="noopener noreferrer">View photo</a></div>
                        )}
                        {verification?.aadharCard?.frontImage?.path && (
                          <div><a href={verificationDocUrl(verification.aadharCard.frontImage.path)} target="_blank" rel="noopener noreferrer">View Aadhar (front)</a></div>
                        )}
                        {verification?.aadharCard?.backImage?.path && (
                          <div><a href={verificationDocUrl(verification.aadharCard.backImage.path)} target="_blank" rel="noopener noreferrer">View Aadhar (back)</a></div>
                        )}
                        {verification?.panCard?.image?.path && (
                          <div><a href={verificationDocUrl(verification.panCard.image.path)} target="_blank" rel="noopener noreferrer">View PAN</a></div>
                        )}
                        {!verification?.customerPhoto?.path &&
                          !verification?.aadharCard?.frontImage?.path &&
                          !verification?.aadharCard?.backImage?.path &&
                          !verification?.panCard?.image?.path && (
                          <span className="text-muted">Abhi upload nahi — Edit profile se documents upload karein.</span>
                        )}
                        <div className="mt-2">
                          <span className={`badge me-2 ${
                            verification?.verificationStatus === 'verified' ? 'bg-success' :
                            verification?.verificationStatus === 'rejected' ? 'bg-danger' : 'bg-secondary'
                          }`}>
                            {verification?.verificationStatus || 'incomplete'}
                          </span>
                          {verification?.verificationStatus !== 'verified' && (
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              disabled={markingKyc}
                              onClick={handleMarkKycVerified}
                            >
                              {markingKyc ? 'Saving…' : 'Mark KYC Verified'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">Purchase Statistics</h5>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-6 col-lg-3 mb-3">
                    <div className="border rounded p-2">
                      <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Total Purchases</h6>
                      <h4 className="mb-0">{customer.totalPurchases || 0}</h4>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-3 mb-3">
                    <div className="border rounded p-2">
                      <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Total Spent</h6>
                      <h4 className="mb-0">₹{customer.totalSpent?.toLocaleString() || '0'}</h4>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-3 mb-3">
                    <div className="border rounded p-2">
                      <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Reward Points</h6>
                      <h4 className="mb-0">{customer.rewardPoints || 0}</h4>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-3 mb-3">
                    <div className="border rounded p-2">
                      <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Active EMIs</h6>
                      <h4 className="mb-0">{pendingEmiData.emiCount || 0}</h4>
                    </div>
                  </div>
                </div>

                {pendingEmiData.totalPendingAmount > 0 && (
                  <div className="alert alert-warning mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="alert-heading mb-1">Total Pending Amount</h6>
                        <h3 className="mb-0">₹{pendingEmiData.totalPendingAmount.toLocaleString()}</h3>
                      </div>
                      <Link to="/emi-dashboard" className="btn btn-outline-dark">
                        View All EMIs
                      </Link>
                    </div>
                  </div>
                )}
                
                {customer.rewardPoints > 0 && (
                  <div className="mt-3 text-center">
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Reward Points Available:</strong> {customer.rewardPoints} points (₹{customer.rewardPoints})
                      <div className="small mt-1">
                        These points can be used directly during purchase. No need to create vouchers!
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Witness Profile Section */}
      {(customer.witnessProfile?.isWitness || customer.witnessedPurchases?.length > 0) && (
        <div className="card mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="bi bi-person-check me-2"></i>
              Witness Profile
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h3>{customer.witnessProfile?.totalReferrals || customer.witnessedPurchases?.length || 0}</h3>
                    <div>Total Referrals</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h3>₹{customer.witnessProfile?.totalReferralAmount?.toLocaleString() || 
                        customer.witnessedPurchases?.reduce((total, p) => total + p.salePrice, 0)?.toLocaleString() || 0}</h3>
                    <div>Total Sales Value</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h3>{customer.rewardPoints}</h3>
                    <div>Witness Reward Points</div>
    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase History Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'customer' ? 'active' : ''}`}
            onClick={() => setActiveTab('customer')}
          >
            <i className="bi bi-cart-check me-1"></i> As Customer
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'seller' ? 'active' : ''}`}
            onClick={() => setActiveTab('seller')}
          >
            <i className="bi bi-phone-flip me-1"></i> As Seller
            {sellerIntakeHistory?.totalUnitsSoldToShop > 0 ? (
              <span className="badge bg-warning text-dark ms-1">{sellerIntakeHistory.totalUnitsSoldToShop}</span>
            ) : null}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'repairs' ? 'active' : ''}`}
            onClick={() => setActiveTab('repairs')}
          >
            <i className="bi bi-tools me-1"></i> Repairs
            {repairJobs.length > 0 ? (
              <span className="badge bg-info text-dark ms-1">{repairJobs.length}</span>
            ) : null}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'pawn' ? 'active' : ''}`}
            onClick={() => setActiveTab('pawn')}
          >
            <i className="bi bi-gem me-1"></i> Pawn
            {pawnHistory?.activeCount > 0 ? (
              <span className="badge bg-warning text-dark ms-1">{pawnHistory.activeCount}</span>
            ) : pawnHistory?.totalCount > 0 ? (
              <span className="badge bg-secondary ms-1">{pawnHistory.totalCount}</span>
            ) : null}
          </button>
        </li>
        {hasWitnessProfile && (
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'witness' ? 'active' : ''}`}
              onClick={() => setActiveTab('witness')}
            >
              <i className="bi bi-person-check me-1"></i> As Witness
            </button>
          </li>
        )}
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'loans' ? 'active' : ''}`}
            onClick={() => setActiveTab('loans')}
          >
            <i className="bi bi-bank me-1"></i> Loan History
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'loan-apply' ? 'active' : ''}`}
            onClick={() => setActiveTab('loan-apply')}
          >
            <i className="bi bi-plus-circle me-1"></i> Apply Loan
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'credit' ? 'active' : ''}`}
            onClick={() => { setActiveTab('credit'); if (!creditData) fetchCreditScore(); }}
          >
            <i className="bi bi-graph-up me-1"></i> Credit Score
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'phone-tests' ? 'active' : ''}`}
            onClick={() => setActiveTab('phone-tests')}
          >
            <i className="bi bi-phone me-1"></i> Phone Testing
            {phoneTests.length > 0 ? <span className="badge bg-secondary ms-1">{phoneTests.length}</span> : null}
          </button>
        </li>
      </ul>

      {activeTab === 'phone-tests' && (
        <div className="card mb-4">
          <div className="card-header"><h5 className="mb-0">Phone Testing</h5></div>
          <div className="card-body">
            <p className="text-muted small">These are device checks only. They are not added to Etawah or Punjab stock.</p>
            {phoneTests.length === 0 ? <p className="mb-0">No saved phone tests for this number.</p> : (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Date</th><th>Report</th><th>Phone</th><th>IMEI</th><th>Grade</th><th>Range</th></tr></thead>
                  <tbody>
                    {phoneTests.map((t) => (
                      <tr key={t.code}>
                        <td>{t.createdAt ? new Date(t.createdAt).toLocaleString('en-IN') : ''}</td>
                        <td><a href={`/testing/#${t.code}`} target="_blank" rel="noreferrer">{t.code}</a></td>
                        <td>{t.brand} {t.model}</td>
                        <td>{t.imei}</td>
                        <td>{t.grade || '—'}</td>
                        <td>{t.recommendedINR ? `₹${Number(t.recommendedINR).toLocaleString('en-IN')}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Purchase History Table - Customer View */}
      {activeTab === 'customer' && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Purchase History</h5>
          </div>
          <div className="card-body">
            {sortedPurchases.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>EMI</th>
                      <th>Witness</th>
                      <th>Sale pic</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPurchases.map((purchase, index) => (
                      <tr key={index}>
                        <td>{new Date(purchase.date).toLocaleDateString()}</td>
                        <td>
                          {purchase.productName}
                          {purchase.variant && <span className="text-muted"> ({purchase.variant})</span>}
                          {(purchase.imeiInfo?.imei1 || purchase.imeiInfo?.imei2) && (
                            <div className="small text-muted mt-1">
                              IMEI: <code>{purchase.imeiInfo.imei1 || '—'}</code>
                              {purchase.imeiInfo.imei2 ? (
                                <>
                                  {' '}
                                  · <code>{purchase.imeiInfo.imei2}</code>
                                </>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td>₹{purchase.salePrice.toLocaleString()}</td>
                        <td>
                          {purchase.isEmi ? (
                            <span className="badge bg-info">EMI</span>
                          ) : (
                            <span className="badge bg-success">One-time</span>
                          )}
                        </td>
                        <td>
                          {renderWitnessLink(purchase.witness)}
                        </td>
                        <td>
                          {(() => {
                            const unitPhotos = (purchase.saleUnitPhotos || []).filter(Boolean);
                            const unitVideo = purchase.saleUnitVideoUrl
                              ? String(purchase.saleUnitVideoUrl).trim()
                              : '';
                            const salePhoto = purchase.salePhotoUrl
                              ? String(purchase.salePhotoUrl).trim()
                              : '';
                            if (!unitPhotos.length && !unitVideo && !salePhoto) {
                              return <span className="text-muted">—</span>;
                            }
                            return (
                              <div className="d-flex gap-1 flex-wrap align-items-center">
                                {unitPhotos.map((src) => (
                                  <a key={src} href={src} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={src}
                                      alt="Unit"
                                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                    />
                                  </a>
                                ))}
                                {unitVideo ? (
                                  <a
                                    href={unitVideo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-danger py-0"
                                    title="Unit condition video"
                                  >
                                    ▶ Video
                                  </a>
                                ) : null}
                                {salePhoto ? (
                                  <a href={salePhoto} target="_blank" rel="noopener noreferrer" title="Sale moment">
                                    <img
                                      src={salePhoto}
                                      alt="Sale"
                                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                    />
                                  </a>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {purchase.isEmi && purchase.emiId && (
                            <Link to={`/emi/${purchase.emiId}`} className="btn btn-sm btn-outline-info me-1">
                              View EMI
                            </Link>
                          )}
                          {purchase.isEmi && purchase.emiId && pendingEmiData.activeEmis?.some(
                            (a) => String(a._id) === String(purchase.emiId)
                          ) && (
                            <Link
                              to={`/emi/${purchase.emiId}`}
                              className="btn btn-sm btn-outline-warning me-1"
                              title="Phone return & close EMI"
                            >
                              Return
                            </Link>
                          )}
                          {/* Manual EMI Completion Button */}
                          {purchase.isEmi && purchase.emiId && (
                            <button
                              className="btn btn-sm btn-outline-warning me-1"
                              onClick={() => handleManualEmiCompletion(purchase.emiId)}
                              title="Mark EMI as Fully Paid (Admin Only)"
                            >
                              <i className="bi bi-check-circle"></i>
                            </button>
                          )}
                          {/* Download and Send buttons for ALL purchases */}
                          <button
                            className="btn btn-sm btn-outline-success me-1"
                            onClick={() => handleDownloadInvoice(purchase.emiId || purchase._id)}
                            disabled={downloadingInvoice}
                            title="Download Invoice PDF"
                          >
                            {downloadingInvoice ? (
                              <i className="bi bi-hourglass-split"></i>
                            ) : (
                              <i className="bi bi-download"></i>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleSendInvoice(purchase.emiId || purchase._id)}
                            disabled={sendingInvoice}
                            title="Send Invoice via WhatsApp"
                          >
                            {sendingInvoice ? (
                              <i className="bi bi-hourglass-split"></i>
                            ) : (
                              <i className="bi bi-whatsapp"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted">No purchase history available</p>
            )}
          </div>
        </div>
      )}

      {/* Sold to shop (Old Mobile intake) */}
      {activeTab === 'seller' && (
        <div className="card mb-4">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">Sold to Shop (Old Mobile)</h5>
          </div>
          <div className="card-body">
            <p className="small text-muted mb-3">
              Jab is customer ne shop ko phone becha (naam + 10 digit mobile IMEI par save) — +100 points aur WhatsApp
              sirf tab jab dono bhare hon. Sirf naam / sirf mobile = record IMEI par, points nahi.
            </p>
            {sellerIntakeHistory?.intakes?.length > 0 ? (
              <>
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <div className="border rounded p-2 text-center">
                      <div className="fw-bold">{sellerIntakeHistory.totalUnitsSoldToShop}</div>
                      <small className="text-muted">Units sold to shop</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 text-center">
                      <div className="fw-bold">{sellerIntakeHistory.totalIntakePointsEarned || 0}</div>
                      <small className="text-muted">Intake points (₹1 = 1 pt)</small>
                    </div>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped table-sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Product</th>
                        <th>IMEI</th>
                        <th>Status</th>
                        <th>Buy ₹</th>
                        <th>Points</th>
                        <th>Intake pic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sellerIntakeHistory.intakes.map((row, idx) => (
                        <tr key={`${row.productId}-${row.imeiIndex}-${idx}`}>
                          <td>
                            {row.sellerIntakeRewardAt
                              ? new Date(row.sellerIntakeRewardAt).toLocaleDateString('en-IN')
                              : row.addedDate
                                ? new Date(row.addedDate).toLocaleDateString('en-IN')
                                : '—'}
                          </td>
                          <td>
                            {row.productName}
                            {row.brand ? <span className="text-muted small d-block">{row.brand}</span> : null}
                          </td>
                          <td>
                            <code className="small">{row.imei1}</code>
                            {row.imei2 ? (
                              <div className="small text-muted">
                                <code>{row.imei2}</code>
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <span className="badge bg-secondary">{row.status}</span>
                          </td>
                          <td>
                            {row.buyingPrice != null ? `₹${Number(row.buyingPrice).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td>
                            {row.pointsAwarded ? (
                              <span className="badge bg-success">+100</span>
                            ) : row.intakeComplete === false ? (
                              <span className="badge bg-light text-dark border" title="Naam/mobile incomplete">
                                Record only
                              </span>
                            ) : (
                              <span className="text-muted">Pending</span>
                            )}
                          </td>
                          <td>
                            {row.sellerIntakePhotoUrl ? (
                              <a href={row.sellerIntakePhotoUrl} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={row.sellerIntakePhotoUrl}
                                  alt="Intake"
                                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                                />
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-muted mb-0">
                Is phone par abhi koi Old Mobile unit seller ke roop mein save nahi mila.
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pawn' && (
        <div className="card mb-4">
          <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Pawn history</h5>
            <Link to="/pawn-app" className="btn btn-sm btn-dark">Pawn counter</Link>
          </div>
          <div className="card-body">
            {pawnHistory?.tickets?.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {pawnHistory.tickets.map((t) => (
                  <div key={t._id} className="border rounded p-3 bg-light">
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                      <div>
                        <code>{t.ticketNumber}</code>
                        <span className="badge bg-secondary ms-2">{t.status}</span>
                      </div>
                      <div className="text-end">
                        {t.status === 'active' && t.redemption ? (
                          <span className="fw-bold">Due {`₹${Number(t.redemption.totalDue).toLocaleString('en-IN')}`}</span>
                        ) : null}
                      </div>
                    </div>
                    <PawnPhotoThumbs urls={t.intakePhotos} size={100} />
                    <div className="row g-2 small mt-2">
                      <div className="col-md-4"><strong>Device:</strong> {t.deviceLabel || t.deviceModel}</div>
                      <div className="col-md-4"><strong>Principal:</strong> ₹{Number(t.principalAmount || 0).toLocaleString('en-IN')}</div>
                      <div className="col-md-4"><strong>Paid out:</strong> ₹{Number(t.disbursementAmount || 0).toLocaleString('en-IN')}</div>
                      <div className="col-md-4"><strong>Pickup:</strong> {t.dueDate ? new Date(t.dueDate).toLocaleDateString('en-IN') : '—'}</div>
                      {t.deviceLock ? (
                        <div className="col-md-4"><strong><i className="bi bi-lock me-1" />Lock:</strong> {t.deviceLock}</div>
                      ) : null}
                      {t.imei ? <div className="col-md-4"><strong>IMEI:</strong> {t.imei}</div> : null}
                      {t.status === 'redeemed' ? (
                        <>
                          <div className="col-md-4"><strong>Collected:</strong> ₹{Number(t.redemptionAmountPaid || 0).toLocaleString('en-IN')} on {t.redeemedAt ? new Date(t.redeemedAt).toLocaleDateString('en-IN') : '—'}</div>
                          {t.systemDueAtRedeem ? <div className="col-md-4"><strong>System due:</strong> ₹{Number(t.systemDueAtRedeem).toLocaleString('en-IN')}</div> : null}
                          {t.collectionNote ? <div className="col-12"><strong>Note:</strong> {t.collectionNote}</div> : null}
                        </>
                      ) : null}
                      {t.status === 'sold' ? (
                        <>
                          <div className="col-md-4"><strong>Sold:</strong> ₹{Number(t.soldAmount || 0).toLocaleString('en-IN')}</div>
                          <div className="col-md-4"><strong>Sold date:</strong> {t.soldAt ? new Date(t.soldAt).toLocaleDateString('en-IN') : '—'}</div>
                          {t.soldNote ? <div className="col-12"><strong>Sale note:</strong> {t.soldNote}</div> : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">No pawn tickets — <Link to="/pawn-app">create one</Link></p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'repairs' && (
        <div className="card mb-4">
          <div className="card-header bg-info text-dark d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Repair History</h5>
            <Link to="/repairs" className="btn btn-sm btn-dark">New repair</Link>
          </div>
          <div className="card-body">
            {repairJobs.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Device</th>
                      <th>Issue</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repairJobs.map((j) => (
                      <tr key={j._id}>
                        <td><code>{j.jobNumber}</code></td>
                        <td>{[j.deviceBrand, j.deviceModel].filter(Boolean).join(' ') || '—'}</td>
                        <td>{j.issueDescription || j.issueCategory}</td>
                        <td><span className="badge bg-secondary">{j.status}</span></td>
                        <td>₹{(j.finalCost || j.estimatedCost || 0).toLocaleString('en-IN')}</td>
                        <td>{j.receivedAt ? new Date(j.receivedAt).toLocaleDateString('en-IN') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted mb-0">Abhi koi repair job nahi — <Link to="/repairs">pehla job add karo</Link></p>
            )}
          </div>
        </div>
      )}

      {/* Witnessed Purchases Table - Witness View */}
      {activeTab === 'witness' && (
        <div className="card mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Witnessed Purchases</h5>
          </div>
          <div className="card-body">
            {sortedWitnessedPurchases && sortedWitnessedPurchases.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>EMI</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWitnessedPurchases.map((purchase, index) => (
                      <tr key={index}>
                        <td>{new Date(purchase.date).toLocaleDateString()}</td>
                        <td>
                          {purchase.customerId ? (
                            <Link to={`/customer/${purchase.customerId}`} className="fw-semibold text-decoration-underline">
                              {purchase.customerName}
                            </Link>
                          ) : (
                            purchase.customerName
                          )}
                          <div><small className="text-muted">{purchase.customerPhone}</small></div>
                        </td>
                        <td>{purchase.productName}</td>
                        <td>₹{purchase.salePrice.toLocaleString()}</td>
                        <td>
                          {purchase.isEmi ? (
                            <span className="badge bg-info">EMI</span>
                          ) : (
                            <span className="badge bg-success">One-time</span>
                          )}
                        </td>
                        <td>
                          {purchase.isEmi && purchase.emiId && (
                            <Link to={`/emi/${purchase.emiId}`} className="btn btn-sm btn-outline-info me-1">
                              View EMI
                            </Link>
                          )}
                          {purchase.customerId && (
                            <Link to={`/customer/${purchase.customerId}`} className="btn btn-sm btn-outline-primary me-1">
                              View Customer
                            </Link>
                          )}
                          {/* Download and Send buttons for ALL witnessed purchases */}
                          <button
                            className="btn btn-sm btn-outline-success me-1"
                            onClick={() => handleDownloadInvoice(purchase.emiId || purchase._id)}
                            disabled={downloadingInvoice}
                            title="Download Invoice PDF"
                          >
                            {downloadingInvoice ? (
                              <i className="bi bi-hourglass-split"></i>
                            ) : (
                              <i className="bi bi-download"></i>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleSendInvoice(purchase.emiId || purchase._id)}
                            disabled={sendingInvoice}
                            title="Send Invoice via WhatsApp"
                          >
                            {sendingInvoice ? (
                              <i className="bi bi-hourglass-split"></i>
                            ) : (
                              <i className="bi bi-whatsapp"></i>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No witnessed purchases found. When this customer acts as a witness for other customers' purchases, they will appear here.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Voucher Redemptions Table */}
      {activeTab === 'customer' && (
        <div className="card">
          <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Voucher Redemption History</h5>
            <span className="badge bg-light text-dark">{sortedRedemptions?.length || 0} Redemptions</span>
          </div>
          <div className="card-body">
            {sortedRedemptions && sortedRedemptions.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-info">
                    <tr>
                      <th>Date & Time</th>
                      <th>Voucher Code</th>
                      <th>Points Used</th>
                      <th>Amount Redeemed</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRedemptions.map((redemption, index) => {
                      const redemptionDate = new Date(redemption.date);
                      return (
                        <tr key={index}>
                          <td>
                            <div className="fw-bold">{redemptionDate.toLocaleDateString()}</div>
                            <small className="text-muted">{redemptionDate.toLocaleTimeString()}</small>
                          </td>
                          <td className="fw-bold">{redemption.voucherCode}</td>
                          <td>
                            <span className="badge bg-warning text-dark">{redemption.pointsValue} points</span>
                          </td>
                          <td className="text-success fw-bold">₹{redemption.amountRedeemed.toLocaleString()}</td>
                          <td>
                            <span className="badge bg-success">Redeemed</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No voucher redemptions found. When the customer redeems vouchers, the history will appear here.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loan History Table */}
      {activeTab === 'loans' && (
        <div className="card">
          <div className="card-header bg-warning text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-bank me-2"></i>
              Loan History
            </h5>
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setActiveTab('loan-apply')}
            >
              <i className="bi bi-plus me-1"></i>
              New Loan
            </button>
          </div>
          <div className="card-body">
            {(customerLoansFull.length > 0 || (customer.loans && customer.loans.length > 0)) ? (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-warning">
                    <tr>
                      <th>Date</th>
                      <th>Loan Type</th>
                      <th>Loan Amount</th>
                      <th>Total Amount</th>
                      <th>Risk Level</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(customerLoansFull.length
                      ? customerLoansFull.map((loan) => ({
                          date: loan.startDate || loan.createdAt || loan.disbursementDate,
                          loanId: loan._id,
                          loanType: loan.loanType,
                          loanAmount: loan.loanAmount,
                          totalAmount: loan.totalAmount,
                          status: loan.status,
                          riskLevel: loan.riskLevel === 'very_high' ? 'high' : loan.riskLevel,
                          payments: loan.payments || [],
                          full: loan,
                        }))
                      : customer.loans
                    ).map((loan, index) => {
                      const lid = String(loan.loanId || loan._id || index);
                      const payments = loan.payments || customerLoansFull.find((l) => String(l._id) === lid)?.payments || [];
                      const paidCount = payments.filter((p) => p.status === 'completed').length;
                      const isOpen = expandedLoanId === lid;
                      return (
                        <React.Fragment key={lid}>
                          <tr>
                            <td>
                              <div className="fw-bold">{loan.date ? new Date(loan.date).toLocaleDateString('en-IN') : '—'}</div>
                            </td>
                            <td>
                              <span className="badge bg-primary">{loan.loanType}</span>
                            </td>
                            <td className="fw-bold">₹{loan.loanAmount?.toLocaleString('en-IN')}</td>
                            <td className="fw-bold">₹{loan.totalAmount?.toLocaleString('en-IN')}</td>
                            <td>
                              <span className={`badge bg-${
                                loan.riskLevel === 'low' ? 'success' :
                                loan.riskLevel === 'medium' ? 'warning' : 'danger'
                              }`}>
                                {loan.riskLevel?.toUpperCase() || '—'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge bg-${
                                loan.status === 'active' ? 'primary' :
                                loan.status === 'completed' ? 'success' :
                                loan.status === 'pending' ? 'warning' :
                                loan.status === 'defaulted' ? 'danger' : 'secondary'
                              }`}>
                                {loan.status?.toUpperCase()}
                              </span>
                              {payments.length > 0 && (
                                <div className="small text-muted mt-1">{paidCount}/{payments.length} EMI paid</div>
                              )}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => setExpandedLoanId(isOpen ? null : lid)}
                                  title="Payment history"
                                >
                                  <i className="bi bi-calendar3 me-1"></i>
                                  {isOpen ? 'Hide' : 'Payments'}
                                </button>
                                <Link
                                  to={`/loan/${lid}`}
                                  className="btn btn-outline-primary"
                                >
                                  <i className="bi bi-eye me-1"></i>
                                  Details
                                </Link>
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="table-light">
                              <td colSpan={7}>
                                <div className="small fw-semibold mb-2">Payment schedule / history</div>
                                {payments.length === 0 ? (
                                  <div className="text-muted">Is loan par abhi koi installment schedule nahi.</div>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0 bg-white">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Due</th>
                                          <th>Paid on</th>
                                          <th>Amount</th>
                                          <th>Method</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[...payments]
                                          .sort((a, b) => (a.installmentNumber || 0) - (b.installmentNumber || 0))
                                          .map((p, i) => (
                                            <tr key={i}>
                                              <td>{p.installmentNumber || i + 1}</td>
                                              <td>{p.dueDate ? new Date(p.dueDate).toLocaleDateString('en-IN') : '—'}</td>
                                              <td>
                                                {p.status === 'completed' && p.date
                                                  ? new Date(p.date).toLocaleDateString('en-IN')
                                                  : '—'}
                                              </td>
                                              <td>₹{Number(p.amount || 0).toLocaleString('en-IN')}</td>
                                              <td>{p.paymentMethod || '—'}</td>
                                              <td>
                                                <span className={`badge bg-${
                                                  p.status === 'completed' ? 'success' :
                                                  p.status === 'overdue' ? 'danger' : 'warning text-dark'
                                                }`}>
                                                  {p.status}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No loan history found. Apply for Loan tab se naya loan apply karein.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loan Application Tab */}
      {activeTab === 'loan-apply' && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="bi bi-plus-circle me-2"></i>
              Apply for Loan - {customer?.name}
            </h5>
          </div>
          <div className="card-body">
            {/* KYC Status Check — uses CustomerVerification (docs) OR kycDetails */}
            <div className="alert alert-info mb-4">
              <h6><i className="bi bi-info-circle me-2"></i>KYC Status Check</h6>
              <p className="mb-2">
                <strong>Documents verification:</strong>
                <span className={`badge ms-2 ${
                  verification?.verificationStatus === 'verified' ? 'bg-success' :
                  verification?.verificationStatus === 'pending' ? 'bg-warning text-dark' :
                  verification?.verificationStatus === 'rejected' ? 'bg-danger' : 'bg-secondary'
                }`}>
                  {verification?.verificationStatus || 'incomplete'}
                </span>
                <span className="ms-3"><strong>Profile KYC:</strong></span>
                <span className={`badge ms-2 ${
                  customer?.kycDetails?.kycStatus === 'verified' ? 'bg-success' :
                  customer?.kycDetails?.kycStatus === 'pending' ? 'bg-warning text-dark' :
                  customer?.kycDetails?.kycStatus === 'rejected' ? 'bg-danger' : 'bg-secondary'
                }`}>
                  {customer?.kycDetails?.kycStatus || 'Not Started'}
                </span>
              </p>
              {!isKycVerified() && (
                <div className="alert alert-warning mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>KYC verification required!</strong> Pehle documents upload karein, phir <strong>Mark KYC Verified</strong> dabao.
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={() => setActiveTab('customer')}
                    >
                      <i className="bi bi-person-check me-1"></i>
                      Upload KYC Docs
                    </button>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      disabled={markingKyc}
                      onClick={handleMarkKycVerified}
                    >
                      {markingKyc ? 'Saving…' : 'Mark KYC Verified'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Loan Application Form */}
            {isKycVerified() && (
              <form onSubmit={handleLoanApplication}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Loan Type *</label>
                      <select 
                        name="loanType" 
                        value={loanForm.loanType} 
                        onChange={handleLoanInputChange}
                        className="form-select"
                        required
                      >
                        <option value="personal">Personal Loan</option>
                        <option value="business">Business Loan</option>
                        <option value="emergency">Emergency Loan</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Loan Purpose *</label>
                      <input
                        type="text"
                        name="loanPurpose"
                        value={loanForm.loanPurpose}
                        onChange={handleLoanInputChange}
                        className="form-control"
                        placeholder="e.g., Medical emergency, Business expansion"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Loan Amount (₹) *</label>
                      <input
                        type="number"
                        name="loanAmount"
                        value={loanForm.loanAmount}
                        onChange={handleLoanInputChange}
                        className="form-control"
                        placeholder="Enter loan amount"
                        min="1000"
                        max="50000"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Tenure (Months) *</label>
                      <select 
                        name="tenure" 
                        value={loanForm.tenure} 
                        onChange={handleLoanInputChange}
                        className="form-select"
                        required
                      >
                        <option value="2">2 Months</option>
                        <option value="3">3 Months</option>
                        <option value="4">4 Months</option>
                        <option value="6">6 Months</option>
                        <option value="9">9 Months</option>
                        <option value="12">12 Months</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Witness Name</label>
                      <input
                        type="text"
                        name="witness.witnessName"
                        value={loanForm.witness.witnessName}
                        onChange={handleLoanInputChange}
                        className="form-control"
                        placeholder="Witness name (optional)"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Witness Phone</label>
                      <input
                        type="text"
                        name="witness.witnessPhone"
                        value={loanForm.witness.witnessPhone}
                        onChange={handleLoanInputChange}
                        className="form-control"
                        placeholder="Witness phone (optional)"
                      />
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loanApplicationLoading}
                  >
                    {loanApplicationLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Submit Loan Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Loan Application Message */}
            {loanApplicationMessage && (
              <div className={`alert ${loanApplicationMessage.includes('🎉') ? 'alert-success' : loanApplicationMessage.includes('❌') ? 'alert-danger' : 'alert-info'} mt-3`}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                  {loanApplicationMessage}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual EMI Completion Modal */}
      {showEmiCompletionModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="bi bi-shield-lock me-2"></i>
                  Manual EMI Completion
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmiCompletionModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Admin Only:</strong> This action will mark the EMI as fully paid. Use only in special cases.
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Enter Admin PIN</label>
                  <input
                    type="password"
                    className="form-control"
                    value={completionPin}
                    onChange={(e) => setCompletionPin(e.target.value)}
                    placeholder="Enter PIN (2838)"
                    maxLength="4"
                  />
                  <div className="form-text">Enter the 4-digit admin PIN to proceed</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEmiCompletionModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleEmiCompletionSubmit}
                  disabled={completingEmi || !completionPin}
                >
                  {completingEmi ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Completing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Mark as Completed
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Credit Score Tab ===== */}
      {activeTab === 'credit' && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><i className="bi bi-graph-up me-2 text-primary"></i>Credit Score — {customer?.name}</h5>
            <button className="btn btn-outline-primary btn-sm" onClick={fetchCreditScore} disabled={creditLoading}>
              {creditLoading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-arrow-clockwise me-1"></i>}
              Refresh
            </button>
          </div>
          <div className="card-body">
            {creditLoading && !creditData && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2 text-muted">Credit score calculate ho raha hai...</p>
              </div>
            )}
            {!creditLoading && !creditData && (
              <div className="text-center py-4">
                <p className="text-muted">Credit score abhi load nahi hua.</p>
                {creditError && <p className="text-danger small">{creditError}</p>}
                <button className="btn btn-primary" onClick={fetchCreditScore}>Load Credit Score</button>
              </div>
            )}
            {creditData && (() => {
              const cs = creditData.creditScore;
              const grade = creditData.creditGrade;
              const maxLoan = creditData.maxLoanAmount;
              const bd = creditData.breakdown || {};
              const gradeColor = grade === 'A+' || grade === 'A' ? 'success' : grade === 'B+' || grade === 'B' ? 'primary' : grade === 'C+' || grade === 'C' ? 'warning' : 'danger';
              const barWidth = Math.round(((cs - 300) / 700) * 100);
              return (
                <div>
                  {/* Score card */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-4 text-center">
                      <div className={`card border-${gradeColor} h-100`}>
                        <div className="card-body">
                          <div className={`display-4 fw-bold text-${gradeColor}`}>{cs}</div>
                          <div className={`badge bg-${gradeColor} fs-5 mb-2`}>Grade {grade}</div>
                          <div className="text-muted small">out of 1000</div>
                          <div className="progress mt-2" style={{ height: 10 }}>
                            <div className={`progress-bar bg-${gradeColor}`} style={{ width: `${barWidth}%` }} />
                          </div>
                          <div className="d-flex justify-content-between small text-muted mt-1">
                            <span>300</span><span>1000</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <h6 className="text-muted mb-3">Max Loan Eligibility</h6>
                          <div className="fs-3 fw-bold text-success">₹{maxLoan?.toLocaleString('en-IN')}</div>
                          <p className="small text-muted mt-2">{creditData.motivationalMessage}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <h6 className="text-muted mb-2">Improvement Tips</h6>
                          <ul className="small ps-3 mb-0">
                            {(creditData.improvementTips || []).map((tip, i) => (
                              <li key={i}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <h6 className="fw-semibold mb-3">Score Breakdown</h6>
                  <div className="row g-3">
                    {[
                      { key: 'emiHistory',      label: 'EMI History',       icon: 'bi-calendar-check', weight: 35, data: bd.emiHistory },
                      { key: 'loanHistory',     label: 'Loan History',      icon: 'bi-bank',           weight: 20, data: bd.loanHistory },
                      { key: 'purchaseBehavior',label: 'Purchase Behavior', icon: 'bi-cart',           weight: 15, data: bd.purchaseBehavior },
                      { key: 'witnessProfile',  label: 'Witness Profile',   icon: 'bi-person-check',   weight: 10, data: bd.witnessProfile },
                      { key: 'customerTenure',  label: 'Customer Tenure',   icon: 'bi-clock-history',  weight: 10, data: bd.customerTenure },
                      { key: 'verification',    label: 'KYC Verification',  icon: 'bi-shield-check',   weight: 10, data: bd.verification },
                    ].map(item => item.data && (
                      <div key={item.key} className="col-md-6">
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="fw-semibold small">
                                <i className={`bi ${item.icon} me-1`}></i>{item.label}
                              </span>
                              <span className="badge bg-secondary">{item.weight}%</span>
                            </div>
                            <div className="progress mb-2" style={{ height: 8 }}>
                              <div
                                className={`progress-bar ${item.data.score >= 70 ? 'bg-success' : item.data.score >= 40 ? 'bg-warning' : 'bg-danger'}`}
                                style={{ width: `${item.data.score}%` }}
                              />
                            </div>
                            <div className="d-flex justify-content-between small">
                              <span className="text-muted">{item.data.details}</span>
                              <span className="fw-bold ms-2">{item.data.score}/100</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {invoiceResult && (
        <div
          className={`alert ${invoiceResult.success ? 'alert-success' : 'alert-danger'} alert-dismissible fade show position-fixed`}
          style={{ top: '20px', right: '20px', zIndex: 9999 }}
        >
          <h6 className="mb-1">{invoiceResult.success ? '📄 Invoice sent' : '❌ Invoice failed'}</h6>
          <p className="mb-0 small">{invoiceResult.message}</p>
          {invoiceResult.invoiceNumber && (
            <p className="mb-0 small">
              <strong>No:</strong> {invoiceResult.invoiceNumber}
            </p>
          )}
          <button type="button" className="btn-close" onClick={() => setInvoiceResult(null)}></button>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;